import { cx } from "@emotion/css";

import { Group } from "@/modules/groups/types";
import { Deadline } from "@/modules/theme/components/Deadline/Deadline";
import { LabelAdornment } from "@/modules/theme/components/Label/LabelAdornment";
import { Link } from "@/modules/theme/components/Link/Link";
import { PointsLabel } from "@/modules/theme/components/PointsLabel";
import { ProgressCircle } from "@/modules/theme/components/ProgressCircle/ProgressCircle";
import { StarsRating } from "@/modules/theme/components/Stars/StarsRating";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { createTaskItemStyles } from "../hooks/createTaskItemStyles";
import { ReactComponent as Arrow } from "../icons/arrow.svg";
import { ReactComponent as Tick } from "../icons/tick.svg";

export type SerializedTaskItem = {
  name: string;
  pointsNumber: number;
  groups?: Group[];
  deadline?: string | null;
  progress?: number;
  done?: boolean;
  rating?: number;
};

export type TaskItemProps = {
  className?: string;
  link: string;
} & SerializedTaskItem;

export const TaskItem = ({
  name,
  pointsNumber,
  groups,
  progress,
  deadline,
  done = false,
  rating,
  className,
  link,
}: TaskItemProps) => {
  const theme = useTheme();
  const classes = createTaskItemStyles(theme);

  return (
    <Link.Internal className={cx(classes.root, className)} to={link}>
      <div
        className={cx(classes.progress, {
          [classes.taskDone]: done,
        })}
      >
        {progress ? (
          <ProgressCircle size={34} progress={progress} strokeWidth={4}>
            <Tick className={classes.tick} />
          </ProgressCircle>
        ) : (
          <Tick className={classes.tick} />
        )}
      </div>
      <div className={classes.content}>
        <div>
          <p className={classes.name}>{name}</p>
          {deadline && <Deadline>{deadline}</Deadline>}
        </div>
        <div className={classes.additionalInfo}>
          <div className={classes.ratingWrapper}>
            <PointsLabel points={pointsNumber} className={classes.points} />
            {rating && <StarsRating value={rating} size={14} />}
          </div>
          {!done && groups && (
            <div className={classes.groupWrapper}>
              {groups.map(({ name, colorHex }) => (
                <LabelAdornment
                  key={name}
                  text={name}
                  color={colorHex}
                  className={classes.group}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={classes.arrowWrapper}>
        <Arrow />
      </div>
    </Link.Internal>
  );
};
